import React, { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Page,
  Card,
  BlockStack,
  Text,
  InlineGrid,
} from "@shopify/polaris";
import { useAppTitleBar } from "../../hooks/useAppTitleBar";
import { CREATION_PLACEMENT_OPTIONS, PRESET_BY_PLACEMENT } from "../../utils/themeEditorLinks";
import ShippingBarPreview, { PLACEMENT_LABELS } from "../../components/settings/ShippingBarPreview";

const PREVIEW_CONTENT = {
  goal: { amount: 50, currency: "USD" },
  messages: {
    initial: "Free shipping for orders over {order-value}!",
    progress: "You're only {order-value-progress} away from free shipping!",
    success: "Congratulations! You've got free shipping!",
  },
};

const PREVIEW_DESIGN = {
  card: {
    background_type: "solid",
    background: "#FFFFFF",
    corner_radius: 8,
    border_size: 1,
    border_color: "#E3E3E3",
    spacing: { inside_top: 16, inside_bottom: 16, outside_top: 12, outside_bottom: 12 },
  },
  progress_bar: { background: "#E4E5E7", fill: "#000000", corner_radius: 4 },
  typography: { message: { size: 16, color: "#202223" } },
  layout: { widget_width_percent: 100 },
};

function PlacementCard({ option, selected, onSelect }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(option.id)}
      className={`fsb-placement-card${selected ? " fsb-placement-card--selected" : ""}`}
    >
      <BlockStack gap="200">
        <Text as="span" variant="headingSm">
          {option.title}
        </Text>
        <Text as="span" variant="bodySm" tone="subdued">
          {option.description}
        </Text>
      </BlockStack>
    </button>
  );
}

export default function ChoosePlacementPage() {
  const navigate = useNavigate();
  const [selected, setSelected] = useState("product_page");

  const previewPlacement = useMemo(() => selected, [selected]);

  useAppTitleBar({
    title: "Choose placement",
    breadcrumb: { label: "Dashboard", onClick: () => navigate("/") },
  });

  const onContinue = useCallback(() => {
    const presetKey = PRESET_BY_PLACEMENT[selected] || "product_inline_minimal";
    navigate("/settings/bars/new", {
      state: { placement_type: selected, preset_key: presetKey },
    });
  }, [navigate, selected]);

  return (
    <Page
      fullWidth
      title="Where should the bar appear?"
      primaryAction={{
        content: "Continue",
        onAction: onContinue,
      }}
    >
      <BlockStack gap="500">
        <InlineGrid columns={{ xs: 1, md: ["oneThird", "twoThirds"] }} gap="500">
          <Card>
            <BlockStack gap="400">
              <Text as="p" variant="bodyMd" tone="subdued">
                Pick one placement to start. You can change settings later in the bar editor.
              </Text>
              <BlockStack gap="300">
                {CREATION_PLACEMENT_OPTIONS.map((option) => (
                  <PlacementCard
                    key={option.id}
                    option={option}
                    selected={selected === option.id}
                    onSelect={setSelected}
                  />
                ))}
              </BlockStack>
            </BlockStack>
          </Card>

          <div className="fsb-editor-preview-sticky">
            <Card>
              <BlockStack gap="200">
                <Text as="h2" variant="headingSm">
                  {PLACEMENT_LABELS[previewPlacement] || "Preview"}
                </Text>
                <ShippingBarPreview
                content={PREVIEW_CONTENT}
                design={PREVIEW_DESIGN}
                upsell={{ mode: "none", products: [] }}
                placementType={previewPlacement}
                previewMode="progress"
                />
              </BlockStack>
            </Card>
          </div>
        </InlineGrid>
      </BlockStack>
    </Page>
  );
}
